import { useEffect, useRef, useState } from "react";
import { Download, Share, Smartphone, X } from "lucide-react";
import { isStandalone } from "@/lib/pwa";

// Minimal shape of the non-standard beforeinstallprompt event.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "sundayatlas_pwa_hint_dismissed";

const isIos = (): boolean => {
  const ua = window.navigator.userAgent;
  // iPhone/iPod are straightforward. iPadOS 13+ reports as "Macintosh",
  // so also treat a touch-capable Mac as iOS for install purposes.
  return (
    /iPhone|iPad|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

const PwaInstallHint = () => {
  const [mode, setMode] = useState<"hidden" | "prompt" | "ios">("hidden");
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Don't nag if already installed or previously dismissed.
    if (isStandalone()) return;
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") return;
    } catch {
      /* localStorage blocked — proceed without dismissal memory */
    }

    // Android / desktop Chrome: capture the native install prompt and
    // surface our own button so the install affordance is discoverable.
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setMode("prompt");
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS has no beforeinstallprompt; show Add-to-Home-Screen guidance.
    // Small delay so it doesn't flash before we know the prompt won't fire.
    const iosTimer = setTimeout(() => {
      if (!deferredPrompt.current && isIos()) setMode("ios");
    }, 1200);

    // If the app gets installed, hide immediately.
    const onInstalled = () => setMode("hidden");
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setMode("hidden");
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  const handleInstall = async () => {
    const evt = deferredPrompt.current;
    if (!evt) return;
    await evt.prompt();
    const choice = await evt.userChoice.catch(() => null);
    deferredPrompt.current = null;
    // Whether they accept or dismiss the native dialog, stop showing ours.
    if (choice?.outcome === "accepted") {
      setMode("hidden");
    } else {
      dismiss();
    }
  };

  if (mode === "hidden") return null;

  return (
    <div className="w-full bg-paper-warm border-b border-rule">
      <div className="mx-auto max-w-5xl px-5 py-2 flex items-center gap-3">
        <div className="flex items-center justify-center shrink-0 text-accent-text">
          <Smartphone className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          {mode === "prompt" ? (
            <p className="kicker text-ink leading-tight">
              Install SundayAtlas
            </p>
          ) : (
            <p className="font-serif text-sm text-ink-muted leading-snug">
              <span className="text-ink">Install SundayAtlas:</span> tap{" "}
              <Share className="inline h-3.5 w-3.5 -mt-0.5" /> then "Add to
              Home Screen"
            </p>
          )}
        </div>
        {mode === "prompt" && (
          <button
            onClick={handleInstall}
            className="kicker shrink-0 inline-flex items-center gap-2 min-h-[44px] px-4 border border-ink text-ink hover:bg-ink hover:text-paper transition-colors"
          >
            <Download className="h-3.5 w-3.5" />
            Install
          </button>
        )}
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="shrink-0 inline-flex h-11 w-11 items-center justify-center text-ink-faint hover:text-ink transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default PwaInstallHint;
